import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './Button';

interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'impressum' | 'privacy' | null;
}

export const LegalModals: React.FC<LegalModalProps> = ({ isOpen, onClose, type }) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !type) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal Content */}
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col relative z-10 animate-fade-in-up">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <h2 className="text-2xl font-black text-brand-dark">
            {type === 'impressum' ? 'Impressum' : 'Datenschutzerklärung'}
          </h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500 hover:text-brand-dark"
          >
            <X size={24} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto p-6 md:p-8 text-gray-600 leading-relaxed space-y-6 text-sm md:text-base">
          
          {type === 'impressum' && (
            <>
              <div>
                <h3 className="font-bold text-brand-dark mb-2">Angaben gemäß § 5 TMG</h3>
                <p>
                  Bethesna Verwaltung GmbH<br />
                  Lankwitzer Straße 9<br />
                  12107 Berlin
                </p>
              </div>

              <div>
                <h3 className="font-bold text-brand-dark mb-2">Vertreten durch</h3>
                <p>Esref Yalcinkaya</p>
              </div>

              <div>
                <h3 className="font-bold text-brand-dark mb-2">Kontakt</h3>
                <p>
                  E-Mail: info@zackig.marketing<br />
                  Web: www.zackig.marketing
                </p>
              </div>

              <div>
                <h3 className="font-bold text-brand-dark mb-2">Registereintrag</h3>
                <p>
                  Eintragung im Handelsregister.<br />
                  Registergericht: Amtsgericht Charlottenburg<br />
                  Registernummer: HRB 261270 B
                </p>
              </div>

              <div>
                <h3 className="font-bold text-brand-dark mb-2">Umsatzsteuer-ID</h3>
                <p>
                  Umsatzsteuer-Identifikationsnummer gemäß § 27 a Umsatzsteuergesetz:<br />
                  DE367349502
                </p>
              </div>

              <div>
                <h3 className="font-bold text-brand-dark mb-2">EU-Streitschlichtung</h3>
                <p>
                  Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: 
                  <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer" className="text-brand-orange hover:underline ml-1">
                    https://ec.europa.eu/consumers/odr/
                  </a>.<br />
                  Unsere E-Mail-Adresse finden Sie oben im Impressum.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-brand-dark mb-2">Verbraucherstreitbeilegung/Universalschlichtungsstelle</h3>
                <p>
                  Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
                </p>
              </div>
            </>
          )}

          {type === 'privacy' && (
            <>
              <div>
                <h3 className="font-bold text-brand-dark mb-2">1. Datenschutz auf einen Blick</h3>
                <p className="mb-2"><strong>Allgemeine Hinweise</strong></p>
                <p>
                  Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-brand-dark mb-2">2. Hosting</h3>
                <p>
                  Wir hosten die Inhalte unserer Website bei einem externen Anbieter. Die personenbezogenen Daten, die auf dieser Website erfasst werden, werden auf den Servern des Hosters gespeichert. Hierbei kann es sich v. a. um IP-Adressen, Kontaktanfragen, Meta- und Kommunikationsdaten, Vertragsdaten, Kontaktdaten, Namen, Websitezugriffe und sonstige Daten, die über eine Website generiert werden, handeln.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-brand-dark mb-2">3. Allgemeine Hinweise und Pflichtinformationen</h3>
                <p className="mb-2"><strong>Datenschutz</strong></p>
                <p className="mb-2">
                  Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre personenbezogenen Daten vertraulich und entsprechend den gesetzlichen Datenschutzvorschriften sowie dieser Datenschutzerklärung.
                </p>
                <p className="mb-2"><strong>Verantwortliche Stelle</strong></p>
                <p>
                  Bethesna Verwaltung GmbH<br/>
                  Lankwitzer Straße 9<br/>
                  12107 Berlin<br/>
                  E-Mail: info@zackig.marketing
                </p>
              </div>

              <div>
                <h3 className="font-bold text-brand-dark mb-2">4. Datenerfassung auf dieser Website</h3>
                <p className="mb-2"><strong>Cookies</strong></p>
                <p className="mb-2">
                  Unsere Internetseiten verwenden so genannte „Cookies“. Cookies sind kleine Textdateien und richten auf Ihrem Endgerät keinen Schaden an. Sie werden entweder vorübergehend für die Dauer einer Sitzung (Session-Cookies) oder dauerhaft (permanente Cookies) auf Ihrem Endgerät gespeichert.
                </p>
                <p className="mb-2"><strong>Kontaktformular</strong></p>
                <p>
                  Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-brand-dark mb-2">5. Analyse-Tools und Werbung</h3>
                <p>
                  Soweit Sie Ihre Einwilligung erklärt haben, wird auf dieser Website Google Analytics eingesetzt, ein Webanalysedienst der Google LLC. Zuständiger Dienstanbieter in der EU ist die Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex justify-end">
          <Button onClick={onClose} variant="primary">
            Schließen
          </Button>
        </div>
      </div>
    </div>
  );
};